import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useIsSimulating } from '@/stores/simulationStore';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { SimulationPanel } from './SimulationPanel';
import { AccountMenu } from './AccountMenu';
import * as authApi from '@/features/auth/auth.api';
import { activeLanguages, getNextLanguageCode } from '@/i18n/config';
import { LAGEN } from '@xxtract/ui';
import { cn } from '@/lib/utils';

interface TopBarProps {
  onToggleSidebar: () => void;
  showMenuButton: boolean;
}

export function TopBar({ onToggleSidebar, showMenuButton }: TopBarProps) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { logoutMutation } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const isSimulating = useIsSimulating();

  const [showSimPanel, setShowSimPanel] = useState(false);
  // FR57: customer-only dropdown that surfaces "Mijn gegevens" (h12-3). Kept separate from
  // showSimPanel (employee-only "view as" popover) — the two never apply to the same role.
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // CAP-6: een employee ziet alleen "Mijn feedback"; "bekijk als klant" haalt klantgegevens op die de
  // server hem weigert, en het klantenmenu (Mijn gegevens) is er niet voor hem. Zijn avatar opent dus niets.
  const isFeedbackOnly = user?.role === 'employee';
  const isEmployee = user?.role === 'admin' || user?.role === 'manager';
  const anyPanelOpen = showSimPanel || showAccountMenu;

  // Click-outside and Escape close whichever panel is open.
  useEffect(() => {
    if (!anyPanelOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setShowSimPanel(false);
        setShowAccountMenu(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSimPanel(false);
        setShowAccountMenu(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anyPanelOpen]);

  const toggleLanguage = () => {
    const newLang = getNextLanguageCode(i18n.resolvedLanguage ?? i18n.language, activeLanguages);
    i18n.changeLanguage(newLang);
    authApi.updatePreferences({ language: newLang }).catch(() => {});
  };

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  const avatarClasses = "flex h-7 w-7 items-center justify-center rounded-lg text-[0.6875rem] font-semibold";
  const avatarStyle = { backgroundColor: 'var(--primary-light)', color: 'var(--primary)' };

  return (
    <header
      data-testid="topbar"
      className={cn('sticky top-0 flex h-14 items-center px-0', LAGEN.kopbalk)}
      style={{
        backgroundColor: 'var(--primary)',
        boxShadow: '0 1px 0 rgba(0,0,0,0.12)',
      }}
    >
      {/* Logo area */}
      {/* Onder 640 px (`sm`) vallen de portalnaam (alleen zichtbaar; een schermlezer leest hem nog)
          en een deel van de binnenruimte weg: met "Extra groot" was de balk op 375 px anders 476 px
          breed. Op 320 px met "Extra groot" stak hij nog 32 px uit (herontwerp Werkkwaliteit, story 5);
          daarom krimpen onder `sm` ook de tussenruimtes. Daarboven is hij ongewijzigd. */}
      <div className="flex w-60 items-center gap-1.5 px-2 sm:gap-2.5 sm:px-5">
        {showMenuButton && (
          <button
            data-testid="menu-toggle"
            onClick={onToggleSidebar}
            className="flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-100 hover:bg-white/10 sm:mr-1 xl:hidden"
            aria-label="Menu"
          >
            <span
              className="material-symbols-outlined"
              style={{ color: 'rgba(255,255,255,0.6)', fontSize: '20px' }}
            >
              menu
            </span>
          </button>
        )}
        <div
          className="flex h-7 w-7 items-center justify-center rounded-[7px]"
          style={{ backgroundColor: 'var(--success)' }}
        >
          <span className="text-xs font-bold text-white">X</span>
        </div>
        <span className="sr-only text-[0.9375rem] font-bold text-white sm:not-sr-only">
          XXtract Portal
        </span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Action buttons */}
      <div className="flex items-center gap-1 px-2 sm:px-4">
        {/* Language toggle */}
        <button
          data-testid="language-toggle"
          onClick={toggleLanguage}
          className="flex h-9 items-center justify-center rounded-lg px-2 text-xs font-medium transition-all duration-100 hover:bg-white/10"
          style={{ color: 'rgba(255,255,255,0.6)' }}
          aria-label={t('common.language')}
        >
          {getNextLanguageCode(i18n.resolvedLanguage ?? i18n.language, activeLanguages).toUpperCase()}
        </button>

        {/* Dark mode toggle */}
        <button
          data-testid="theme-toggle"
          onClick={toggleTheme}
          className="flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-100 hover:bg-white/10"
          aria-label={t('user.theme')}
        >
          <span
            className="material-symbols-outlined"
            style={{ color: 'rgba(255,255,255,0.6)', fontSize: '18px' }}
          >
            {isDark ? 'light_mode' : 'dark_mode'}
          </span>
        </button>

        {/* User menu */}
        <div
          data-testid="user-menu"
          className="relative ml-1 flex items-center gap-2 sm:ml-2"
          ref={panelRef}
        >
          {isFeedbackOnly ? (
            <span data-testid="user-avatar" className={avatarClasses} style={avatarStyle} aria-hidden="true">
              {initials}
            </span>
          ) : isEmployee ? (
            <button
              data-testid="user-avatar"
              onClick={() => setShowSimPanel((prev) => !prev)}
              className={avatarClasses}
              style={{ ...avatarStyle, cursor: 'pointer' }}
              aria-haspopup="dialog"
              aria-expanded={showSimPanel}
              aria-label={t('simulation.viewAs')}
            >
              {initials}
            </button>
          ) : (
            <button
              data-testid="user-avatar"
              onClick={() => setShowAccountMenu((prev) => !prev)}
              className={avatarClasses}
              style={{ ...avatarStyle, cursor: 'pointer' }}
              aria-haspopup="menu"
              aria-expanded={showAccountMenu}
              aria-label={t('nav.myAccount')}
            >
              {initials}
            </button>
          )}
          <div className="hidden md:block">
            <div className="text-[0.8125rem] font-medium text-white">
              {user?.name}
            </div>
            <div
              className="text-[0.625rem]"
              style={{ color: 'rgba(255,255,255,0.42)' }}
            >
              {isSimulating
                ? t('simulation.active')
                : user?.role
                  ? t(`users.roles.${user.role}`)
                  : ''}
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => logoutMutation.mutate()}
            className="flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-100 hover:bg-white/10 sm:ml-2"
            aria-label={t('auth.logout')}
          >
            <span
              className="material-symbols-outlined"
              style={{ color: 'rgba(255,255,255,0.6)', fontSize: '18px' }}
            >
              logout
            </span>
          </button>

          {showSimPanel && isEmployee && (
            <SimulationPanel onClose={() => setShowSimPanel(false)} />
          )}
          {showAccountMenu && !isEmployee && !isFeedbackOnly && (
            <AccountMenu onClose={() => setShowAccountMenu(false)} />
          )}
        </div>
      </div>
    </header>
  );
}
